import React, { useState, useRef, useEffect } from 'react';
import { Popover, Select, Checkbox, Input, Button, Space, Segmented, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    CHARTS, CONDITIONS, SURFACES, isPrimaryTooth, isGone,
    conditionColor, conditionLabel, describeTooth, translate
} from '../../utils/dental';
import ToothShape, { toothKind, TOOTH_WIDTH } from './ToothShape';
import './ToothChart.css';

// The tooth chart: the mouth as the dentist sees the patient, the patient's
// right on the left of the screen. Press a tooth to say what is wrong with it.
//
// A form control - it takes `value` and `onChange` like any input, so the
// visit form holds the chart the way it holds everything else. `value` is the
// list of teeth something has been said about; a tooth nobody has touched is
// simply not in it.
//
//   readOnly   the chart is for looking at (the patient page)
//   compact    smaller teeth, no legend (a visit in the history)

const ToothChart = ({ value, onChange, readOnly = false, compact = false }) => {
    const { t } = useTranslation();
    const chart = Array.isArray(value) ? value : [];
    const byTooth = new Map(chart.map((entry) => [entry.tooth, entry]));

    // Open on the milk teeth when that is where the marks are - a child's
    // chart should not open on an empty adult mouth.
    const [set, setSet] = useState(() => (
        chart.length > 0 && chart.every((entry) => isPrimaryTooth(entry.tooth)) ? 'primary' : 'permanent'
    ));
    const [openTooth, setOpenTooth] = useState(null);
    const frame = useRef(null);

    // On a screen too narrow for the whole jaw the chart scrolls sideways. It
    // opens on the front teeth - the middle of the mouth - rather than on the
    // patient's right molars with everything else out of sight.
    useEffect(() => {
        const node = frame.current;
        if (node && node.scrollWidth > node.clientWidth) {
            node.scrollLeft = (node.scrollWidth - node.clientWidth) / 2;
        }
    }, [set]);

    const tr = (key, fallback) => translate(t, key, fallback);

    const update = (tooth, changes) => {
        if (!onChange) return;
        const next = { ...(byTooth.get(tooth) || { tooth }), ...changes };
        const empty = !next.condition && !next.note && !(next.surfaces?.length > 0);
        const rest = chart.filter((entry) => entry.tooth !== tooth);
        onChange(empty ? rest : [...rest, next].sort((a, b) => a.tooth.localeCompare(b.tooth)));
    };

    const clear = (tooth) => {
        if (onChange) onChange(chart.filter((entry) => entry.tooth !== tooth));
        setOpenTooth(null);
    };

    const editor = (tooth) => {
        const entry = byTooth.get(tooth) || {};
        return (
            <div className="tooth-editor">
                <Select
                    allowClear
                    autoFocus
                    style={{ width: '100%' }}
                    placeholder={tr('toothCondition', 'Condition')}
                    value={entry.condition}
                    onChange={(condition) => update(tooth, { condition })}
                    options={CONDITIONS.map(([key, labelKey, fallback, color]) => ({
                        value: key,
                        label: (
                            <Space size={6}>
                                <span className="tooth-dot" style={{ background: color }} />
                                {tr(labelKey, fallback)}
                            </Space>
                        )
                    }))}
                />
                <Checkbox.Group
                    className="tooth-surfaces"
                    value={entry.surfaces || []}
                    onChange={(surfaces) => update(tooth, { surfaces })}
                    options={SURFACES.map(([letter, labelKey, fallback]) => ({
                        value: letter,
                        label: <span title={tr(labelKey, fallback)}>{letter}</span>
                    }))}
                />
                <Input
                    placeholder={t('notes')}
                    value={entry.note}
                    onChange={(event) => update(tooth, { note: event.target.value })}
                    onPressEnter={() => setOpenTooth(null)}
                />
                <div className="tooth-editor-actions">
                    <Button size="small" danger type="text" onClick={() => clear(tooth)}>
                        {tr('toothClear', 'Clear')}
                    </Button>
                    <Button size="small" type="primary" onClick={() => setOpenTooth(null)}>
                        {tr('done', 'Done')}
                    </Button>
                </div>
            </div>
        );
    };

    const tooth = (number) => {
        const entry = byTooth.get(number);
        const color = conditionColor(entry?.condition);
        const marked = Boolean(entry);

        const button = (
            <button
                type="button"
                key={number}
                className={[
                    'tooth',
                    `tooth-${toothKind(number)}`,
                    marked ? 'tooth-marked' : '',
                    isGone(entry?.condition) ? 'tooth-gone' : '',
                    openTooth === number ? 'tooth-open' : '',
                    readOnly ? 'tooth-readonly' : ''
                ].join(' ')}
                // Molars are drawn wider than incisors, as they are.
                style={{ '--tooth-width': TOOTH_WIDTH[toothKind(number)] }}
                title={marked ? describeTooth(t, entry) : number}
                aria-label={marked ? describeTooth(t, entry) : `${tr('tooth', 'Tooth')} ${number}`}
                disabled={readOnly && !marked}
            >
                <ToothShape
                    number={number}
                    condition={entry?.condition}
                    surfaces={entry?.surfaces}
                    color={color}
                    note={Boolean(entry?.note)}
                />
                <span className="tooth-number" style={marked && color ? { color, fontWeight: 700 } : undefined}>{number}</span>
            </button>
        );

        if (readOnly) return button;

        return (
            <Popover
                key={number}
                trigger="click"
                placement="bottom"
                title={`${tr('tooth', 'Tooth')} ${number}`}
                open={openTooth === number}
                onOpenChange={(open) => setOpenTooth(open ? number : null)}
                content={editor(number)}
                destroyOnHidden
            >
                {button}
            </Popover>
        );
    };

    const [upper, lower] = CHARTS[set];
    // Only the conditions actually on the chart, so the legend explains what
    // is on screen rather than listing everything a tooth could be.
    const used = CONDITIONS.filter(([key]) => chart.some((entry) => entry.condition === key));
    const marks = (which) => chart.filter((entry) => isPrimaryTooth(entry.tooth) === (which === 'primary')).length;

    return (
        <div className={`tooth-chart ${compact ? 'tooth-chart-compact' : ''}`}>
            <div className="tooth-chart-head">
                <Segmented
                    size="small"
                    value={set}
                    onChange={setSet}
                    options={[
                        { value: 'permanent', label: `${tr('teethPermanent', 'Adult')}${marks('permanent') ? ` (${marks('permanent')})` : ''}` },
                        { value: 'primary', label: `${tr('teethPrimary', 'Child')}${marks('primary') ? ` (${marks('primary')})` : ''}` }
                    ]}
                />
                {!readOnly && <span className="tooth-chart-hint">{tr('toothChartHint', 'Press a tooth to mark it')}</span>}
            </div>

            {/* Always left to right: the chart is a picture of the mouth, and a
                mouth does not mirror because the page is in Kurdish. */}
            <div className="tooth-chart-mouth" dir="ltr" ref={frame}>
                <div className="tooth-chart-inner">
                    <div className="tooth-side-labels">
                        <span>{tr('patientRight', 'Right')}</span>
                        <span>{tr('patientLeft', 'Left')}</span>
                    </div>
                    {[upper, lower].map((jaw, index) => (
                        <div className={`tooth-jaw ${index === 0 ? 'tooth-jaw-upper' : 'tooth-jaw-lower'}`} key={index === 0 ? 'upper' : 'lower'}>
                            <div className="tooth-quadrant">{jaw[0].map(tooth)}</div>
                            <div className="tooth-midline" />
                            <div className="tooth-quadrant">{jaw[1].map(tooth)}</div>
                        </div>
                    ))}
                </div>
            </div>

            {!compact && used.length > 0 && (
                <div className="tooth-legend">
                    {used.map(([key, , , color]) => (
                        <Tag key={key} bordered={false} style={{ background: `${color}22`, color: '#1f2a37' }}>
                            <span className="tooth-dot" style={{ background: color }} /> {conditionLabel(t, key)}
                        </Tag>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ToothChart;
